import { Text, Tooltip } from '@/design-system/components/ui';

import { maskAddressValue } from '../../../common/utils/format';

export interface UserIdentifierCellProps {
  userIdentifier: string;
  email?: string | null;
  twitterUsername?: string | null;
}

/**
 * The "User" cell of the All Users table (FUU-1999).
 *
 * Mirrors the All Affiliates cell at
 * `affiliate-management/components/AffiliateTable.tsx:113-127`: display name in
 * the regular table font (`body-sm`, medium), masked address in muted text
 * underneath, full identifier in a tooltip. The previous cell used
 * `Text variant="title-xs"`, which is the oversized font the ticket flags.
 *
 * Deliberate divergence from the analog: that cell falls back to the masked
 * address on BOTH lines when an affiliate has no alias, printing the same string
 * twice. A user has no alias field at all, so the second line is dropped
 * whenever it would only repeat the first.
 */
export function UserIdentifierCell({ userIdentifier, email, twitterUsername }: UserIdentifierCellProps) {
  const masked = maskAddressValue(userIdentifier);
  const displayName = email || twitterUsername || masked;
  const showAddressLine = displayName !== masked && displayName !== userIdentifier;

  return (
    <div className="tw:flex tw:flex-col tw:gap-1">
      <Text variant="body-sm" weight="medium" className="tw:break-words">
        {displayName}
      </Text>
      {showAddressLine && (
        <Tooltip arrow side="top" content={userIdentifier}>
          <span className="tw:text-muted-foreground tw:w-fit tw:whitespace-nowrap">{masked}</span>
        </Tooltip>
      )}
    </div>
  );
}
